import {
  ApolloClient,
  InMemoryCache,
  TypePolicies,
  ApolloLink,
  HttpLink,
} from "@apollo/client";
// import { BatchHttpLink } from "@apollo/client/link/batch-http";
import { createPersistedQueryLink } from "@apollo/client/link/persisted-queries";
import { relayStylePagination } from "@apollo/client/utilities";
import { createUploadLink } from "apollo-upload-client";
import { sha256 } from "crypto-hash";

import { coalesce } from "@kazoohr/helpers";

import { customClientFetch } from "./customClientFetch";

export const mergeIncoming = (_: any, incoming: any) => incoming;

// New versions of Apollo don't add things to the normalized cache if the
// objects don't have IDs. (How could it?) So we have to tell Apollo where it's
// acceptable to add things to the cache.
export const applicationTypePolicies: TypePolicies = {
  directory_User: {
    fields: {
      points: { merge: true },
    },
  },
  admin_SubItem: { keyFields: ["id", "url"] },
  admin_NavigationItem: { keyFields: ["id", "url"] },
  admin_EmbeddedRoute: { keyFields: ["route", "embedUrl"] },
  team_TeamMember: { keyFields: ["login"] },
  /* eslint-disable @typescript-eslint/naming-convention  */
  activityFeed_RecognitionActivity: {
    keyFields: ["id", "timestamp"],
    fields: { id: { merge: mergeIncoming } },
  },
  /* eslint-disable @typescript-eslint/naming-convention  */
  activityFeed_activities: {
    keyFields: ["id", "timestamp"],
    fields: { id: { merge: mergeIncoming } },
  },
  Query: {
    fields: {
      admin_navigation: { merge: true },
      layout_companyBranding: { merge: true },
      profile_profileVisibility: { merge: true },
      activityFeed_activities: { merge: mergeIncoming },
    },
  },
};

export const apiTypePolicies: TypePolicies = {
  Query: {
    fields: {
      incentivesCatalogs: relayStylePagination(["filter", "sort"]),
    },
  },
  /**
   * We need these to cache things properly. The prompts are tied to the main entity (I.E. Feedback Form, not Feedback).
   * So, when you cache them without surveyId, they overwrite each other when clicking around, especially in Check-Ins.
   */
  SurveyShortAnswerPrompt: {
    keyFields: ["id", "surveyId"],
  },
  SurveyRatingScalePrompt: {
    keyFields: ["id", "surveyId"],
  },
  SurveyMultipleChoicePrompt: {
    keyFields: ["id", "surveyId"],
  },
  SurveyElement: {
    keyFields: ["id", "surveyId"],
  },
};

function getHttpLink(uri: string, correlationId: string): ApolloLink {
  const headers = {
    "X-Request-ID": correlationId,
    "Apollo-Require-Preflight": "true",
  };
  const mutationLinkOpts = { uri, headers };
  const queryLinkOpts = { uri, headers, fetch: customClientFetch };

  const persistedQueryLink = createPersistedQueryLink({ sha256 });
  const mutationHttpLink = new HttpLink(mutationLinkOpts);
  const queryHttpLink = new HttpLink(queryLinkOpts);

  const uploadLink = createUploadLink(queryLinkOpts);
  const queryLink = persistedQueryLink.concat(queryHttpLink);
  const mutationLink = persistedQueryLink.concat(mutationHttpLink);

  const nonUploadLink = ApolloLink.split(
    (op) =>
      op.query.definitions.some(
        (d: any) =>
          d.kind === "OperationDefinition" && d.operation === "mutation"
      ),
    mutationLink,
    queryLink
  );

  return ApolloLink.split(
    // istanbul ignore next - apollo internals
    ({ variables }) => hasFiles(variables),
    // ⚠️  This is not actually a type mismatch, the `apollo-upload-client` crew
    //     can't figure out how to properly type this so it supports any version
    //     of @apollo/client you have installed.
    //     - https://github.com/jaydenseric/apollo-upload-client/issues/312
    //     - https://github.com/jaydenseric/apollo-upload-client/issues/285
    //     - and so on...
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    uploadLink as unknown as ApolloLink,
    nonUploadLink
  );
}

/**
 * Recursively check if any of the variables contain a file. Complex because
 * there are a lot of early returns to ensure the complex stuff doesn't run
 * unless necessary.
 */
// eslint-disable-next-line complexity
export function hasFiles(variables?: any): boolean {
  if (!variables || typeof variables !== "object") {
    return false;
  }
  const keys = Object.keys(variables);
  for (const key of keys) {
    const node = variables[key];
    if (!node || typeof node !== "object") {
      continue;
    }

    if (
      (typeof File !== "undefined" && node instanceof File) ||
      (typeof Blob !== "undefined" && node instanceof Blob)
    ) {
      return true;
    }

    if (hasFiles(node)) {
      return true;
    }
  }

  return false;
}

export function setupClient(options: { requestId: string }) {
  const state = coalesce(window?.kazoo?.state, { api: {}, application: {} });

  const apiClient = new ApolloClient({
    link: getHttpLink("/api", options.requestId),
    cache: new InMemoryCache({
      typePolicies: apiTypePolicies,
    }).restore(state.api),
  });

  const client = new ApolloClient({
    link: getHttpLink("/graphql", options.requestId),
    cache: new InMemoryCache({
      typePolicies: applicationTypePolicies,
    }).restore(state.application),
  });

  return {
    client,
    apiClient,
  };
}
